import { Button, Checkbox, Tooltip } from 'antd';
import { css } from '@emotion/css';
import { useState } from 'react';
import { useTranslate } from '@tolgee/react';
import type { ProductKnowledgeScopeProduct } from '../../model/types';
import { ProductThumbImage } from '../ProductThumbImage';
import {
  skuSelectionKey as getScopeSkuKey,
  productSelectionKey as getScopeSpuKey,
} from '../../model/selection';

/**
 * 商品卡片（编辑知识「扩展更多商品」的列表样式），供 DocumentScopeEditor 与
 * 任务创建的商品选择抽屉共用
 */
export const scopeProductCardClassName = css`
  display: flex;
  flex-direction: column;
  min-width: 0;
  height: 100%;
  padding: 12px;
  border: 1px solid var(--fett-border-default);
  border-radius: 8px;
  transition: border-color 0.2s, background-color 0.2s;

  &:hover {
    border-color: var(--ant-primary-color, #3366ff);
  }

  &.document-scope-product-card-selected {
    border-color: var(--ant-primary-color, #3366ff);
    background: var(--fett-primary-bg-active, rgba(51, 102, 255, 0.04));
  }

  .document-scope-product-check {
    display: flex;
    align-items: center;
  }

  .document-scope-product-image {
    flex: 0 0 40px;
    width: 40px;
    height: 40px;
    border-radius: 6px;
    object-fit: cover;
    background: var(--fett-bg-secondary, #f5f5f5);
  }

  .document-scope-product-header {
    display: flex;
    gap: 8px;
    align-items: center;
    min-width: 0;
  }

  .document-scope-product-header-text {
    min-width: 0;
    flex: 1;
  }

  .document-scope-product-title {
    display: block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 600;
    line-height: 20px;
  }

  .document-scope-product-meta {
    display: block;
    margin-top: 2px;
    max-width: 100%;
    /* 店铺/ID/货号是寻址关键信息，换行完整展示而不是省略号截断 */
    color: var(--fett-text-secondary, #8c8c8c);
    font-size: 12px;
    line-height: 18px;
    word-break: break-all;
  }

  .document-scope-product-footer {
    display: flex;
    justify-content: flex-end;
    margin-top: 4px;
  }

  .document-scope-product-footer .ant-btn-link {
    padding: 0;
    height: auto;
    color: var(--fett-text-secondary, #8c8c8c);
  }

  /* SKU 两列排布：单元格 min-width:0 保证长名在列内省略，hover 由外层 Tooltip 显示全名 */
  .document-scope-sku-list {
    margin-top: 8px;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 6px 12px;
  }

  /* antd 的「相邻 checkbox margin-left: 8px」是给行内排列用的，网格里同样错位，这里归零 */
  .document-scope-sku-list .ant-checkbox-wrapper + .ant-checkbox-wrapper {
    margin-left: 0;
  }

  .document-scope-sku {
    min-width: 0;
    max-width: 100%;
  }

  /* 省略号落在 antd 的文本 span 上（wrapper 是 flex，自身 text-overflow 不生效） */
  .document-scope-sku .ant-checkbox + span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export function ProductSkuCard({
  product,
  selected,
  showSkus = true,
  disabled = false,
  onToggleSku,
  onToggleSpu,
  expanded,
  onToggleExpanded,
}: {
  product: ProductKnowledgeScopeProduct;
  selected: Set<string>;
  /** 任务创建场景不展示 SKU 明细，勾选商品即默认选中全部 SKU */
  showSkus?: boolean;
  disabled?: boolean;
  onToggleSku: (key: string) => void;
  onToggleSpu: (product: ProductKnowledgeScopeProduct) => void;
  /** 受控展开（编辑抽屉「展开 SKU」全局切换）；不传时卡片内部自管 */
  expanded?: boolean;
  onToggleExpanded?: (product: ProductKnowledgeScopeProduct) => void;
}) {
  const { t } = useTranslate();
  // 无 SKU 的商品按 SPU 粒度整体勾选，否则复选框永远无法选中
  const skuKeys = product.skus.length
    ? product.skus.map((sku) => getScopeSkuKey(product, sku.skuId))
    : [getScopeSpuKey(product)];
  const spuKey = getScopeSpuKey(product);
  // 商品粒度勾选（不展示 SKU）时直接以 SPU key 判定选中态
  const selectedCount = showSkus
    ? skuKeys.filter((key) => selected.has(key)).length
    : selected.has(spuKey)
    ? skuKeys.length
    : 0;
  const allSelected = skuKeys.length > 0 && selectedCount === skuKeys.length;
  const title =
    product.title || t('productKnowledge_scope_editor_product_fallback', { id: product.spuId });
  const [localExpanded, setLocalExpanded] = useState(false);
  const skuExpanded = expanded ?? localExpanded;
  const handleToggleExpanded = () => {
    if (onToggleExpanded) onToggleExpanded(product);
    else setLocalExpanded((current) => !current);
  };
  const storeLabel = `${product.platform}-${product.region}-${
    product.shopName || product.shopAlias || product.shopId
  }`;

  return (
    <section
      className={`${scopeProductCardClassName}${
        selectedCount ? ' document-scope-product-card-selected' : ''
      }`}
    >
      <div className="document-scope-product-header">
        <div className="document-scope-product-check">
          <Checkbox
            aria-label={t('productKnowledge_scope_editor_select_product_aria', { title })}
            disabled={disabled}
            checked={allSelected}
            indeterminate={selectedCount > 0 && !allSelected}
            onChange={() => onToggleSpu(product)}
          />
        </div>
        {product.imageUrl ? (
          <ProductThumbImage
            className="document-scope-product-image"
            image={product.imageUrl}
            platform={product.platform}
          />
        ) : null}
        <div className="document-scope-product-header-text">
          <Tooltip title={title}>
            <strong className="document-scope-product-title">{title}</strong>
          </Tooltip>
          <span className="document-scope-product-meta">
            {t('productKnowledge_scope_editor_store_info', {
              store: storeLabel,
              // 平台翻译模板占位符是 {shopId}（i18n:pull 拉回），代码曾传 productId
              // 导致 ICU 缺参整行渲染为 "invalid"；两个名字都传，模板漂移免疫
              shopId: product.spuId,
              productId: product.spuId,
              sellerSpu: product.sellerSpu || '-',
            })}
          </span>
        </div>
      </div>
      {showSkus ? (
        <>
          <div className="document-scope-product-footer">
            <Button type="link" size="small" onClick={handleToggleExpanded}>
              {t('productKnowledge_scope_editor_sku_expand', { count: product.skus.length })}{' '}
              {skuExpanded ? '∧' : '∨'}
            </Button>
          </div>
          {skuExpanded ? (
            <div className="document-scope-sku-list">
              {product.skus.map((sku) => {
                const key = getScopeSkuKey(product, sku.skuId);
                const label = sku.skuName || sku.sellerSku || sku.skuId;
                return (
                  <Tooltip key={key} title={label}>
                    <Checkbox
                      className="document-scope-sku"
                      disabled={disabled}
                      checked={selected.has(key)}
                      onChange={() => onToggleSku(key)}
                    >
                      {label}
                    </Checkbox>
                  </Tooltip>
                );
              })}
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
